import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { usersAPI, tenantAPI } from "../../api/client";
import api from "../../api/client";
import { formatDuration } from "../../utils/formatDuration";
import { formatProjectStatus } from "../../utils/formatProjectStatus";
import { formatLocalDate, todayString } from "../../utils/dateUtils";
import DailyBreakdown from "../../components/users/DailyBreakdown";
import "../../components/users/UserActivityReportModal.css";

// Local, not UTC — toISOString() reads back a day early for any timezone ahead
// of UTC, which silently shifts the default range and the `max` bound.
function todayStr() {
  return todayString();
}
function daysAgoStr(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return formatLocalDate(d);
}

const STATUS_LABEL = {
  DONE: "Done",
  APPROVED: "Done",
  SUBMITTED: "Submitted",
  REJECTED: "Rejected",
  RETURNED: "Returned",
  IN_PROGRESS: "In Progress",
  PENDING: "Not Started",
  ASSIGNED: "Assigned",
  REASSIGNED: "Reassigned",
  REMOVED: "Removed",
  SKIPPED: "Skipped",
};
// An unmapped status is title-cased rather than passed through, so a status the
// report has not been taught yet reads as "Some Status", never SOME_STATUS.
function statusLabel(status) {
  if (!status) return "—";
  if (STATUS_LABEL[status]) return STATUS_LABEL[status];
  return String(status)
    .toLowerCase()
    .split(/[_s]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

const STATUS_CLASS = {
  DONE: "uar-status-done",
  APPROVED: "uar-status-done",
  SUBMITTED: "uar-status-submitted",
  REJECTED: "uar-status-rejected",
  RETURNED: "uar-status-returned",
  IN_PROGRESS: "uar-status-active",
  ASSIGNED: "uar-status-active",
  REASSIGNED: "uar-status-active",
};

const PROJECT_FIELDS = [
  { key: "customerName", label: "Customer Name" },
  { key: "workflowName", label: "Workflow" },
  { key: "status", label: "Project Status" },
  { key: "hoursLogged", label: "Hours Logged" },
];

function formatFieldLabel(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
}

function StatCard({ label, value, accent }) {
  return (
    <div
      className={`uar-stat-card${accent ? ` uar-stat-card--${accent}` : ""}`}
    >
      <div className="uar-stat-label">{label}</div>
      <div className="uar-stat-value">{value}</div>
    </div>
  );
}

function formatHoursMinsLabel(minutes) {
  const total = Math.round(Number(minutes) || 0);
  const hrs = Math.floor(total / 60);
  const mins = total % 60;
  if (hrs <= 0) return `${mins}mins`;
  if (mins <= 0) return `${hrs}hr${hrs !== 1 ? "s" : ""}`;
  return `${hrs}hr${hrs !== 1 ? "s" : ""} ${mins}mins`;
}

// Axis labels are day-month (26-08), not the ISO string's month-day — an
// American-looking "08-26" reads as the wrong date here.
function dayMonthLabel(isoDate) {
  const [, m, d] = String(isoDate).split("-");
  return m && d ? `${d}-${m}` : String(isoDate);
}

// Colours match the PDF report's charts.
const CHART_COLORS = {
  stage: "#3b82f6",
  projectTask: "#14b8a6",
  nonProjectTask: "#f59e0b",
};

// Stacked bar per day. `series` is [{ label, color, minutesOf(d) }], bottom
// first; the label above each bar is the day's total.
function HoursPerDayChart({ data, series, emptyText }) {
  if (!data || data.length === 0)
    return (
      <div className="uar-empty">
        {emptyText || "No activity logged in this period."}
      </div>
    );
  const totalOf = (d) => series.reduce((sum, s) => sum + s.minutesOf(d), 0);

  const PAD_L = 32,
    PAD_B = 28,
    PAD_T = 12,
    PAD_R = 12;
  const H = 180;
  const hoursOf = (d) => totalOf(d) / 60;
  const maxHours = Math.max(...data.map(hoursOf), 1);
  const yMax = maxHours <= 4 ? Math.ceil(maxHours) + 1 : Math.ceil(maxHours * 1.15);

  const tickStep = Math.max(1, Math.ceil(yMax / 5));
  const yTicks = [];
  for (let v = 0; v <= yMax; v += tickStep) yTicks.push(v);

  const chartH = H - PAD_T - PAD_B;
  const barSlot = Math.max(10, 560 / data.length);
  const barW = Math.max(4, Math.min(32, barSlot * 0.6));
  const svgW = PAD_L + data.length * barSlot + PAD_R;
  const nth = Math.max(1, Math.ceil(data.length / 20));

  return (
    <div className="uar-chart-scroll">
      <svg
        width={Math.max(svgW, 400)}
        height={H}
        style={{ display: "block", overflow: "visible" }}
        aria-label="Hours logged per day"
      >
        {yTicks.map((tick) => {
          const gy = PAD_T + chartH - (tick / yMax) * chartH;
          return (
            <g key={tick}>
              <line
                x1={PAD_L}
                y1={gy}
                x2={svgW - PAD_R}
                y2={gy}
                stroke={tick === 0 ? "#d1d5db" : "#f0f0f0"}
                strokeWidth="1"
              />
              <text
                x={PAD_L - 6}
                y={gy + 4}
                textAnchor="end"
                fontSize="10"
                fill="#9ca3af"
              >
                {tick}
              </text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const total = totalOf(d);
          const hours = total / 60;
          const barH = Math.max(2, (hours / yMax) * chartH);
          const bx = PAD_L + i * barSlot + (barSlot - barW) / 2;
          const by = PAD_T + chartH - barH;
          const showLabel = i % nth === 0;
          const tooltip = [
            `${d.date}: ${formatDuration(total)}`,
            ...(series.length > 1
              ? series
                  .filter((s) => s.minutesOf(d) > 0)
                  .map((s) => `${s.label}: ${formatDuration(s.minutesOf(d))}`)
              : []),
          ].join("\n");
          let segBottom = PAD_T + chartH;
          return (
            <g key={d.date}>
              <title>{tooltip}</title>
              {total === 0 && (
                <rect x={bx} y={by} width={barW} height={barH} rx="3" fill="#e5e7eb" />
              )}
              {series.map((s) => {
                const minutes = s.minutesOf(d);
                if (!minutes) return null;
                const segH = (minutes / 60 / yMax) * chartH;
                segBottom -= segH;
                return (
                  <rect
                    key={s.label}
                    x={bx}
                    y={segBottom}
                    width={barW}
                    height={segH}
                    fill={s.color}
                    opacity="0.85"
                  />
                );
              })}
              {hours > 0 && (
                <text
                  x={bx + barW / 2}
                  y={by - 3}
                  textAnchor="middle"
                  fontSize="8"
                  fill="#6b7280"
                >
                  {formatHoursMinsLabel(total)}
                </text>
              )}
              {showLabel && (
                <text
                  x={bx + barW / 2}
                  y={PAD_T + chartH + 14}
                  textAnchor="middle"
                  fontSize="9"
                  fill="#9ca3af"
                >
                  {dayMonthLabel(d.date)}
                </text>
              )}
            </g>
          );
        })}
        <line
          x1={PAD_L}
          y1={PAD_T}
          x2={PAD_L}
          y2={PAD_T + chartH}
          stroke="#e5e7eb"
          strokeWidth="1"
        />
        <line
          x1={PAD_L}
          y1={PAD_T + chartH}
          x2={svgW - PAD_R}
          y2={PAD_T + chartH}
          stroke="#e5e7eb"
          strokeWidth="1"
        />
        <text
          x={10}
          y={PAD_T + chartH / 2}
          textAnchor="middle"
          fontSize="9"
          fill="#9ca3af"
          transform={`rotate(-90, 10, ${PAD_T + chartH / 2})`}
        >
          hours
        </text>
      </svg>
    </div>
  );
}

function ChartLegend({ series }) {
  if (series.length < 2) {
    return <span className="uar-section-hint">Hover a bar to see details</span>;
  }
  return (
    <span className="uar-chart-legend">
      {series.map((s) => (
        <span key={s.label} className="uar-chart-legend-item">
          <span
            className="uar-chart-legend-swatch"
            style={{ background: s.color }}
          />
          {s.label}
        </span>
      ))}
    </span>
  );
}

const STAGE_SERIES = {
  label: "Stages",
  color: CHART_COLORS.stage,
  minutesOf: (d) => d.minutesLogged || 0,
};
const PROJECT_TASK_SERIES = {
  label: "Project tasks",
  color: CHART_COLORS.projectTask,
  minutesOf: (d) => d.projectTaskMinutes || 0,
};
const NON_PROJECT_SERIES = [
  {
    label: "Tasks",
    color: CHART_COLORS.nonProjectTask,
    minutesOf: (d) => d.minutes || 0,
  },
];

export default function UserActivityReportPage() {
  const { userId } = useParams();
  const location = useLocation();

  const [user, setUser] = useState(location.state?.user || null);
  const [userLoading, setUserLoading] = useState(!location.state?.user);

  const [from, setFrom] = useState(daysAgoStr(29));
  const [to, setTo] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [showStageFilter, setShowStageFilter] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [stageView, setStageView] = useState("summary");
  const [customFieldKeys, setCustomFieldKeys] = useState([]);

  useEffect(() => {
    tenantAPI
      .getTenant()
      .then((resp) => {
        const fields = resp?.data?.projectFormSchema?.fields;
        if (Array.isArray(fields)) {
          setCustomFieldKeys(
            fields.map((f) => ({ key: f.key, label: f.label })),
          );
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) {
      api
        .get("/users")
        .then((resp) => {
          const found = resp.data.users.find(
            (u) => String(u.id) === String(userId),
          );
          setUser(found || null);
        })
        .catch(() => {})
        .finally(() => setUserLoading(false));
    }
  }, [userId, user]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError("");
    try {
      const resp = await usersAPI.getActivityReport(userId, from, to);
      setData(resp.data);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [userId, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDownload = async (selectedStageNames, selectedFields) => {
    if (!user) return;
    setShowStageFilter(false);
    setDownloading(true);
    try {
      const resp = await usersAPI.downloadActivityReport(
        userId,
        from,
        to,
        selectedStageNames,
        selectedFields,
      );
      const url = URL.createObjectURL(
        new Blob([resp.data], { type: "application/pdf" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `${user.firstName}-${user.lastName}-activity-${from}-to-${to}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const setPreset = (days) => {
    setFrom(daysAgoStr(days - 1));
    setTo(todayStr());
  };

  const {
    summary,
    projects,
    stages,
    stagesByName,
    dailyActivity,
    nonProjectTaskDaily,
    dailyBreakdown,
    currentActivity,
    taskTime,
  } = data || {};

  // Project tasks are stacked on the stage bars only when there is any.
  const projectSeries =
    summary?.projectTaskMinutes > 0
      ? [STAGE_SERIES, PROJECT_TASK_SERIES]
      : [STAGE_SERIES];

  const TABS = [
    { id: "overview", label: "Overview" },
    { id: "projects", label: "Projects" },
    { id: "stages", label: "Stages" },
    { id: "daily", label: "Daily" },
    { id: "tasks", label: "Tasks" },
  ];

  if (userLoading) {
    return (
      <div className="uar-loading">
        <div className="uar-spinner" />
        Loading…
      </div>
    );
  }

  return (
    <div style={{ padding: "16px 24px", maxWidth: 1100, margin: "0 auto" }}>
      <Link className="back-link" to="/admin/users">
        ← Back to User Management
      </Link>

      <div className="uar-header" style={{ marginTop: 16 }}>
        <div className="uar-header-info">
          <div className="uar-header-avatar">
            {user?.firstName?.[0]}
            {user?.lastName?.[0]}
          </div>
          <div>
            <h2 className="uar-header-name">
              {user ? `${user.firstName} ${user.lastName}` : `User ${userId}`}
            </h2>
            {user?.email && (
              <div className="uar-header-email">{user.email}</div>
            )}
          </div>
        </div>
      </div>

      <div className="uar-controls" style={{ marginTop: 16 }}>
        <div className="uar-presets">
          <button className="uar-preset-btn" onClick={() => setPreset(7)}>
            7d
          </button>
          <button className="uar-preset-btn" onClick={() => setPreset(30)}>
            30d
          </button>
          <button className="uar-preset-btn" onClick={() => setPreset(90)}>
            90d
          </button>
        </div>
        <div className="uar-date-range">
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
          />
          <span className="uar-date-sep">→</span>
          <input
            type="date"
            value={to}
            min={from}
            max={todayStr()}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
        <button
          className="um-btn um-btn-primary uar-download-btn"
          onClick={() => handleDownload([], [])}
          disabled={downloading || loading || !data}
        >
          {downloading ? "Downloading…" : "↓ Download PDF"}
        </button>
        <button
          className="um-btn um-btn-secondary uar-download-btn"
          onClick={() => setShowStageFilter(true)}
          disabled={downloading || loading || !data}
        >
          ↓ Download Selected
        </button>
      </div>

      <div
        className="uar-body"
        style={{ marginTop: 0, overflow: "visible", flex: "none" }}
      >
        {error && <div className="um-error">{error}</div>}

        {currentActivity && (
          <div className="uar-active-banner">
            <span className="uar-active-dot" />
            <span>
              Currently on <strong>{currentActivity.stageName}</strong> in{" "}
              <strong>{currentActivity.projectName}</strong>
            </span>
          </div>
        )}

        {loading ? (
          <div className="uar-loading">
            <div className="uar-spinner" />
            Loading report…
          </div>
        ) : data ? (
          <>
            <div className="uar-stats-grid">
              <StatCard
                label="Total Stages"
                value={summary.totalStagesWorked}
              />
              <StatCard
                label="Done"
                value={summary.stagesDone}
                accent="green"
              />
              <StatCard
                label="Submitted"
                value={summary.stagesSubmitted}
                accent="blue"
              />
              <StatCard
                label="Rework"
                value={summary.stagesRejected}
                accent="red"
              />
              <StatCard
                label="In Progress"
                value={summary.stagesActive}
                accent="orange"
              />
              <StatCard label="Projects" value={summary.totalProjects} />
              <StatCard
                label="Hours Logged"
                value={formatDuration(summary.totalMinutesLogged)}
                accent="blue"
              />
              <StatCard label="Active Days" value={summary.activeDays} />
              {summary.additionalWorkMinutes > 0 && (
                <StatCard
                  label="Additional Work"
                  value={formatDuration(summary.additionalWorkMinutes)}
                  accent="orange"
                />
              )}
              {summary.taskMinutesLogged > 0 && (
                <StatCard
                  label="Task Hours"
                  value={formatDuration(summary.taskMinutesLogged)}
                  accent="blue"
                />
              )}
            </div>

            <div className="uar-tabs">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  className={`uar-tab-btn${activeTab === tab.id ? " uar-tab-btn--active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === "overview" && (
              <div className="uar-section">
                <div className="uar-section-header">
                  <span className="uar-section-title">
                    Project Hours Per Day
                  </span>
                  <ChartLegend series={projectSeries} />
                </div>
                <HoursPerDayChart data={dailyActivity} series={projectSeries} />

                {nonProjectTaskDaily?.length > 0 && (
                  <>
                    <div
                      className="uar-section-header"
                      style={{ marginTop: 24 }}
                    >
                      <span className="uar-section-title">
                        Non-Project Task Hours Per Day
                      </span>
                      <span className="uar-section-hint">
                        Tasks not attached to any project
                      </span>
                    </div>
                    <HoursPerDayChart
                      data={nonProjectTaskDaily}
                      series={NON_PROJECT_SERIES}
                    />
                  </>
                )}
              </div>
            )}

            {activeTab === "projects" && (
              <div className="uar-section">
                {!projects?.length ? (
                  <div className="uar-empty">
                    No project activity in this period.
                  </div>
                ) : (
                  <div className="uar-table-wrap">
                    <table className="uar-table">
                      <thead>
                        <tr>
                          <th>Project</th>
                          <th>Customer</th>
                          <th>Workflow</th>
                          <th>Status</th>
                          {customFieldKeys.map((f) => (
                            <th key={f.key}>{f.label}</th>
                          ))}
                          <th>Done</th>
                          <th>Submitted</th>
                          <th>Rework</th>
                          <th>Active</th>
                          <th>Hours</th>
                          <th>Share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projects.map((p) => {
                          const maxMin = Math.max(
                            ...projects.map((x) => x.totalMinutes),
                            1,
                          );
                          const pct = Math.round(
                            (p.totalMinutes / maxMin) * 100,
                          );
                          return (
                            <tr key={p.id}>
                              <td className="uar-project-name">{p.name}</td>
                              <td className="uar-muted">
                                {p.customerName || "—"}
                              </td>
                              <td className="uar-muted">
                                {p.workflowName || "—"}
                              </td>
                              <td className="uar-muted">{p.status ? formatProjectStatus(p.status) : "—"}</td>
                              {customFieldKeys.map((f) => (
                                <td key={f.key} className="uar-muted">
                                  {p.customFields?.[f.key] != null
                                    ? String(p.customFields[f.key])
                                    : "—"}
                                </td>
                              ))}
                              <td>
                                <span className="uar-badge uar-badge-green">
                                  {p.stagesDone}
                                </span>
                              </td>
                              <td>
                                <span className="uar-badge uar-badge-blue">
                                  {p.stagesSubmitted}
                                </span>
                              </td>
                              <td>
                                <span className="uar-badge uar-badge-red">
                                  {p.stagesRejected}
                                </span>
                              </td>
                              <td>
                                <span className="uar-badge uar-badge-orange">
                                  {p.stagesActive}
                                </span>
                              </td>
                              <td className="uar-hours">
                                {formatDuration(p.totalMinutes)}
                              </td>
                              <td>
                                <div className="uar-inline-bar">
                                  <div
                                    className="uar-inline-bar-fill"
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === "stages" && (
              <div className="uar-section">
                <div className="uar-stage-toggle">
                  <button
                    className={`uar-toggle-btn${stageView === "summary" ? " uar-toggle-btn--active" : ""}`}
                    onClick={() => setStageView("summary")}
                  >
                    By Stage Type
                  </button>
                  <button
                    className={`uar-toggle-btn${stageView === "detail" ? " uar-toggle-btn--active" : ""}`}
                    onClick={() => setStageView("detail")}
                  >
                    All Assignments
                  </button>
                </div>

                {stageView === "summary" &&
                  (!stagesByName?.length ? (
                    <div className="uar-empty">
                      No stage activity in this period.
                    </div>
                  ) : (
                    <div className="uar-table-wrap">
                      <table className="uar-table">
                        <thead>
                          <tr>
                            <th>Stage</th>
                            <th>Times Worked</th>
                            <th>Projects</th>
                            <th>Done</th>
                            <th>Rework</th>
                            <th>Hours</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stagesByName.map((s) => (
                            <tr key={s.stageName}>
                              <td className="uar-stage-name">{s.stageName}</td>
                              <td>
                                <span className="uar-badge uar-badge-purple">
                                  {s.timesWorked}
                                </span>
                              </td>
                              <td className="uar-muted">{s.projectCount}</td>
                              <td>
                                <span className="uar-badge uar-badge-green">
                                  {s.stagesDone}
                                </span>
                              </td>
                              <td>
                                <span className="uar-badge uar-badge-red">
                                  {s.stagesRejected}
                                </span>
                              </td>
                              <td className="uar-hours">
                                {formatDuration(s.totalMinutes)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}

                {stageView === "detail" &&
                  (!stages?.length ? (
                    <div className="uar-empty">
                      No stage activity in this period.
                    </div>
                  ) : (
                    <div className="uar-table-wrap">
                      <table className="uar-table">
                        <thead>
                          <tr>
                            <th>Stage</th>
                            <th>Project</th>
                            <th>Status</th>
                            <th>Expected</th>
                            <th>Hours</th>
                            <th>Assigned</th>
                            <th>Completed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stages.map((s) => (
                            <tr key={s.stageAssignmentId}>
                              <td className="uar-stage-name">{s.stageName}</td>
                              <td className="uar-project-name">
                                {s.projectName}
                              </td>
                              <td>
                                <span
                                  className={`uar-status-pill ${STATUS_CLASS[s.status] || ""}`}
                                >
                                  {statusLabel(s.status)}
                                </span>
                              </td>
                              <td className="uar-hours">
                                {s.expectedMinutes != null ? formatDuration(s.expectedMinutes) : "—"}
                              </td>
                              <td className="uar-hours">
                                {formatDuration(s.totalMinutes)}
                              </td>
                              <td className="uar-date">
                                {s.assignedAt
                                  ? new Date(s.assignedAt).toLocaleDateString()
                                  : "—"}
                              </td>
                              <td className="uar-date">
                                {s.completedAt
                                  ? new Date(s.completedAt).toLocaleDateString()
                                  : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
              </div>
            )}

            {activeTab === "daily" && (
              <div className="uar-section">
                <DailyBreakdown days={dailyBreakdown} />
              </div>
            )}

            {activeTab === "tasks" && (
              <div className="uar-section">
                <p className="uar-muted" style={{ margin: "0 0 12px" }}>
                  Time logged on tasks in this period
                  {taskTime?.totalMinutes
                    ? ` — ${formatDuration(taskTime.totalMinutes)} in total`
                    : ""}
                  . Reported separately from stage hours.
                </p>
                {!taskTime?.tasks?.length ? (
                  <div className="uar-empty">No task time logged in this period.</div>
                ) : (
                  <div className="uar-table-wrap">
                    <table className="uar-table">
                      <thead>
                        <tr>
                          <th>Task</th>
                          <th>Projects</th>
                          <th>Status</th>
                          <th>Entries</th>
                          <th>Hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {taskTime.tasks.map((t) => (
                          <tr key={t.taskId}>
                            <td className="uar-stage-name">{t.name}</td>
                            <td className="uar-project-name">
                              {t.projects.map((p) => p.name).join(", ") || "—"}
                            </td>
                            <td>
                              <span
                                className={`uar-badge ${
                                  t.statusLabel === "Rejected"
                                    ? "uar-badge-red"
                                    : t.completed
                                      ? "uar-badge-green"
                                      : "uar-badge-purple"
                                }`}
                              >
                                {t.statusLabel}
                              </span>
                            </td>
                            <td className="uar-muted">{t.entries}</td>
                            <td className="uar-hours">
                              {formatDuration(t.minutes)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        ) : null}
      </div>

      {showStageFilter && (
        <StageFilterModal
          stagesByName={stagesByName || []}
          customFieldKeys={customFieldKeys}
          onConfirm={handleDownload}
          onClose={() => setShowStageFilter(false)}
        />
      )}
    </div>
  );
}

function CheckRow({ checked, onChange, label, hint }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        cursor: "pointer",
        fontSize: 13,
        padding: "6px 8px",
        borderRadius: 6,
        background: checked ? "#eff6ff" : "#f9fafb",
        border: "1px solid",
        borderColor: checked ? "#bfdbfe" : "#e5e7eb",
      }}
    >
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span style={{ flex: 1, fontWeight: 500 }}>{label}</span>
      {hint && <span style={{ fontSize: 11, color: "#9ca3af" }}>{hint}</span>}
    </label>
  );
}

function StageFilterModal({
  stagesByName,
  customFieldKeys = [],
  onConfirm,
  onClose,
}) {
  const allNames = stagesByName.map((s) => s.stageName);
  const allStaticFieldKeys = PROJECT_FIELDS.map((f) => f.key);
  const allCustomFieldKeys = customFieldKeys.map((f) => `customField:${f.key}`);
  const allFieldKeys = [...allStaticFieldKeys, ...allCustomFieldKeys];

  const [selectedStages, setSelectedStages] = useState(new Set(allNames));
  const [selectedFields, setSelectedFields] = useState(new Set(allFieldKeys));

  const toggleStage = (name) => {
    const next = new Set(selectedStages);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setSelectedStages(next);
  };

  const toggleField = (key) => {
    const next = new Set(selectedFields);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelectedFields(next);
  };

  const allStagesChecked = selectedStages.size === allNames.length;
  const allFieldsChecked = selectedFields.size === allFieldKeys.length;

  const SectionHeader = ({ title, allChecked, onToggle }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 8,
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
        {title}
      </span>
      <button
        type="button"
        className="uar-toggle-btn uar-toggle-btn--active"
        style={{ fontSize: 12, padding: "2px 10px" }}
        onClick={onToggle}
      >
        {allChecked ? "Deselect All" : "Select All"}
      </button>
    </div>
  );

  return (
    <div className="um-dialog-overlay" role="dialog" aria-modal="true">
      <div className="um-dialog um-modal-content" style={{ maxWidth: 520 }}>
        <div className="um-dialog-head">
          <h2>Customise PDF Download</h2>
          <button
            className="um-close-btn"
            type="button"
            onClick={onClose}
            aria-label="Close"
          >
            x
          </button>
        </div>
        <div
          className="um-dialog-body"
          style={{ display: "flex", flexDirection: "column", gap: 20 }}
        >
          <div>
            <SectionHeader
              title="Stages"
              allChecked={allStagesChecked}
              onToggle={() =>
                setSelectedStages(
                  allStagesChecked ? new Set() : new Set(allNames),
                )
              }
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {stagesByName.map((s) => (
                <CheckRow
                  key={s.stageName}
                  checked={selectedStages.has(s.stageName)}
                  onChange={() => toggleStage(s.stageName)}
                  label={s.stageName}
                  hint={`${s.timesWorked} assignment${s.timesWorked !== 1 ? "s" : ""}`}
                />
              ))}
            </div>
          </div>

          <div>
            <SectionHeader
              title="Project Fields"
              allChecked={allFieldsChecked}
              onToggle={() =>
                setSelectedFields(
                  allFieldsChecked ? new Set() : new Set(allFieldKeys),
                )
              }
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {PROJECT_FIELDS.map((f) => (
                <CheckRow
                  key={f.key}
                  checked={selectedFields.has(f.key)}
                  onChange={() => toggleField(f.key)}
                  label={f.label}
                />
              ))}
              {customFieldKeys.length > 0 && (
                <>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#9ca3af",
                      marginTop: 4,
                      marginBottom: 2,
                      paddingLeft: 2,
                    }}
                  >
                    Custom Fields
                  </div>
                  {customFieldKeys.map((f) => (
                    <CheckRow
                      key={`customField:${f.key}`}
                      checked={selectedFields.has(`customField:${f.key}`)}
                      onChange={() => toggleField(`customField:${f.key}`)}
                      label={f.label}
                    />
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
        <div className="um-dialog-footer">
          <button
            className="um-btn um-btn-primary"
            onClick={() =>
              onConfirm(Array.from(selectedStages), Array.from(selectedFields))
            }
            disabled={selectedStages.size === 0}
          >
            Download PDF
            {selectedStages.size < allNames.length
              ? ` (${selectedStages.size} of ${allNames.length} stages)`
              : ""}
          </button>
          <button
            className="um-btn um-btn-secondary"
            type="button"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
